// mui
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  Typography,
  Box,
  Card,
  CardHeader,
  Radio,
  Divider,
} from '@mui/material';
import { styled } from '@mui/material/styles';
// hooks
import useResponsive from '../../hooks/useResponsive';
// components
import Iconify from '../../components/Iconify';
import Image from '../../components/Image';
import SvgIconStyle from '../../components/SvgIconStyle';
import DIOText from '../../components/DIOText';
// utils
import { fKrMDEEEEKST, fKrDeadlineDateHourKST } from '../../utils/formatTime';

const RedText = styled('span')(({ theme }) => ({
  color: theme.palette.error.main,
  fontWeight: theme.typography.fontWeightBold,
}));


export default function OfferAccept({
  timeRange,
  workType,
  comment,
  salary,
  trialSalary,
  longtermSalary,
  suggestion,
  questionAnswers,
  status,
  workingHours,
  trialWorkingHours,
  longtermWorkingHours,
  careers,
  trialSelected,
  longtermSelected,
  meetingDeadline = null,
  coffeeChatTime = null
}) {
  const smUp = useResponsive('up', 'sm');
  const before3WayStatus = ["offer-waitlisted", "auto-preprocessing", "offer-to-company", "reselect-3way-time", "waiting-3way-meeting", "confirm-3way-meeting"];
  const negotiateStatus = ["reselect-3way-time", "other-3way-time-requested", "waiting-3way-meeting", "waiting-offer-from-crew", "confirm-3way-meeting", "crew-negotiate-to-company", "company-negotiate-rewrite", "accept"];
  const expanded = !negotiateStatus.includes(status);

  const deadlineDays = meetingDeadline ? Math.max(Number.parseInt((new Date(meetingDeadline).getTime() - new Date().getTime()) / (1000 * 3600 * 24)), 2) : null;
  const averageDuration = deadlineDays > 2 ? "2일" : "하루";

  const planSelected = negotiateStatus.includes(status);
  const plan4weeksSelected = planSelected && !trialSelected && !longtermSelected;
  const planTrialSelected = planSelected && trialSelected && !longtermSelected;
  const planLongtermSelected = planSelected && !trialSelected && longtermSelected;

  const careerDisplayText = (career) => {
    const careerDuration = career.duration ? ` / ${career.duration}` : '';
    return `${career.period}${careerDuration}`;
  };

  const isResidentWork = workType === "resident";

  return (
    <>
      {["auto-preprocessing", "offer-to-company"].includes(status) && (
      <>
        <Typography variant="h4" gutterBottom>
          회사의 결정을 기다려주세요.
        </Typography>
        <Typography variant="body1">
          작성하신 조건을 회사에 전달했습니다.
        </Typography>
        <Typography variant="body1" sx={{ mb: 3 }}>
          회사가 제안을 수락하면 카카오톡으로 미팅 일정을 보내드릴게요.
        </Typography>
        {meetingDeadline && (
          <Box
            sx={{ p: { xs: 3, sm: 5 }, borderRadius: 2, backgroundColor: "grey.200", textAlign: "center" }}
          >
            <Typography variant="h4">
              회사가 <RedText>{deadlineDays}일 안에</RedText>{!smUp && <br />} 검토를 마칠거에요.
            </Typography>
            <Typography variant={smUp ? "body1" : "body2"} sx={{ color: "text.secondary" }}>
              회사의 내부 팀원들과 상의 후 {!smUp && <br />}결정까지 평균 {averageDuration} 이상이 소요됩니다
            </Typography>
            <Image
              visibleByDefault
              disabledEffect
              src={`https://spacey-dio-public.s3.ap-northeast-2.amazonaws.com/images/crew_match_deadline_${deadlineDays}days.png`}
              srcSet={`
                https://spacey-dio-public.s3.ap-northeast-2.amazonaws.com/images/crew_match_deadline_${deadlineDays}days.png 1x,
                https://spacey-dio-public.s3.ap-northeast-2.amazonaws.com/images/crew_match_deadline_${deadlineDays}days%402x.png 2x,
                https://spacey-dio-public.s3.ap-northeast-2.amazonaws.com/images/crew_match_deadline_${deadlineDays}days%403x.png 3x,
              `}
              alt="match soon"
              sx={{
                width: '100%', height: smUp ? '180px' : '140px', my: 2
              }}
            />
            <Typography variant={smUp ? "body1" : "body2"}>
              회사의 검토 마감 기한: {!smUp && <br />}<b>{fKrDeadlineDateHourKST(meetingDeadline)}</b>
            </Typography>
            <Typography variant={smUp ? "body1" : "body2"} sx={{ color: "text.secondary" }}>
              검토 기한까지 회사의 응답이 없으면 이 매칭은 자동으로 취소되고, 다른 회사와 매칭됩니다.
            </Typography>
          </Box>
        )}
      </>
      )}
    <Accordion
      sx={{
        p: 0,
      }}
      defaultExpanded={!meetingDeadline && !coffeeChatTime}>
      <AccordionSummary
        sx={{
          my: 1,
          pt: 1,
          '&.Mui-expanded': {
            my: 0,
            pt: 0,
          }
        }}
        expandIcon={<Iconify icon={'eva:arrow-ios-downward-fill'} width={20} height={20} />}
      >
        <Typography variant="h6" sx={{ color: 'text.secondary' }}>나의 소개{(coffeeChatTime && !trialSalary && !salary) ? "" : " 및 제안"}</Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 0 }}>
      <Card sx={{ mb: 3 }}>
        {!coffeeChatTime && trialSalary > 0 && salary > 0 && (
        <CardHeader title="나의 제안" titleTypographyProps={{ variant: "h5" }} />
        )}
        <Box sx={{ p: 3 }}>
          {careers?.length > 0 && (
            <Stack sx={{ width: 1, mb: 3 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <SvgIconStyle src={`/icons/ic-job.svg`} sx={{ color: expanded ? "secondary.main" : "text.disabled" }} />
                <Typography variant="h6">
                  경력 사항
                </Typography>
              </Stack>
              <Stack sx={{ pl: 4 }} spacing={2} divider={<Divider />} >
                {careers?.map((career, index) => (
                  <Stack key={index} spacing={1}>
                    <Typography variant="subtitle1">
                      {career.name}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                      {careerDisplayText(career)}
                    </Typography>
                    {career.description && (
                      <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                        {career.description}
                      </Typography>
                    )}
                  </Stack>
                ))}
              </Stack>
            </Stack>
          )}
          {comment && (
            <Stack sx={{ mb: 3 }} spacing={1}>
              <Stack direction="row" spacing={1} alignItems="center">
                <SvgIconStyle src={`/icons/ic_person.svg`} sx={{ color: expanded ? "secondary.main" : "text.disabled" }} />
                <Typography variant="h6">
                  본인 소개
                </Typography>
              </Stack>
              <Typography sx={{ pl: 4, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }} variant="body1">
                {comment}
              </Typography>
            </Stack>
          )}
          {/* before trial */}
          {!trialWorkingHours && workingHours > 0 && (
            <Stack sx={{ mb: 3 }} spacing={1}>
              <Stack direction="row" spacing={1} alignItems="center">
                <SvgIconStyle src={`/icons/ic_clock.svg`} sx={{ color: expanded ? "secondary.main" : "text.disabled" }} />
                <Typography variant="h6">
                  주당 업무 가능한 시간
                </Typography>
              </Stack>
              <Typography sx={{ pl: 4 }} variant="body1">
                {workingHours}시간
              </Typography>
            </Stack>
          )}
          {!trialSalary && salary > 0 && (
            <Stack sx={{ mb: 3 }} spacing={1}>
              <Stack direction="row" spacing={1} alignItems="center">
                <SvgIconStyle src={`/icons/icon-coin-mono.svg`} sx={{ color: expanded ? "secondary.main" : "text.disabled" }} />
                <Typography variant="h6">
                  4주 인건비
                </Typography>
              </Stack>
              <Typography sx={{ pl: 4 }} variant="body1">
                {salary}만원
              </Typography>
            </Stack>
          )}
          {/* with trial */}
          {!longtermSalary && !longtermWorkingHours && trialSalary > 0 && trialWorkingHours > 0 && (
            <Stack sx={{ mb: 3 }} spacing={1}>
              <Stack direction="row" spacing={1} alignItems="center">
                <SvgIconStyle src={`/icons/ic_receipt.svg`} sx={{ color: expanded ? "secondary.main" : "text.disabled" }} />
                <Typography variant="h6">
                  업무 조건
                </Typography>
              </Stack>
              <Typography sx={{ pl: 4 }} variant={smUp ? "body1" : "body2"}>
                업무 조건은 사전 미팅 이후 최종 제안 시 변경할 수 있습니다.
              </Typography>
              <Stack sx={{ pl: 4, width: 1}} direction={{ xs: "column", sm: "row" }} alignItems="flex-start" spacing={2}>
                {/* 4주 플랜 */}
                <Box
                  sx={{
                    width: 1,
                    p: 3,
                    borderRadius: 2,
                    border: "1px solid rgba(145, 158, 171, 0.24)",
                    ...plan4weeksSelected && {
                      border: (theme) => `2px solid ${theme.palette.secondary.main}`,
                    },
                  }}
                >
                  <Stack direction="row" alignItems="center" sx={{ mb: 1 }} justifyContent="space-between">
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography variant={smUp ? "h5" : "subtitle1"}>
                        4주 계약
                      </Typography>
                      <Typography variant="subtitle2" sx={{ color: "text.disabled" }}>
                        플랜{!planSelected && (' 적용시')}
                      </Typography>
                    </Stack>
                    {planSelected && (<Radio color="secondary" value="4weeks" checked={plan4weeksSelected} />)}
                  </Stack>
                  <Typography variant="body2" sx={{ color: "text.secondary", mb: 3 }}>
                    설정한 4주 인건비와 주당 업무 시간이에요.<br/>
                    인건비와 주당 업무 시간은 미팅 때 협의하여 변경할 수 있어요.
                  </Typography>
                  <Stack spacing={.5} sx={{ mb: 3 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <SvgIconStyle src={`/icons/icon-coin-mono.svg`} sx={{ color: "text.disabled" }} />
                      <Typography variant="h6">
                        인건비
                      </Typography>
                    </Stack>
                    <Stack direction="row" sx={{ pl: 4 }} spacing={1}>
                      <Typography sx={{ color: "secondary.main" }} variant="subtitle1">
                        {salary}만원
                      </Typography>
                      <Typography sx={{ color: "text.secondary" }} variant="body2">
                        (4주 기준)
                      </Typography>
                    </Stack>
                  </Stack>
                  <Stack spacing={.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <SvgIconStyle src={`/icons/ic_clock.svg`} sx={{ color: "text.disabled" }} />
                      <Typography variant="h6">
                        주당 업무 가능한 시간
                      </Typography>
                    </Stack>
                    <Stack direction="row" sx={{ pl: 4 }} spacing={1}>
                      <Typography sx={{ color: "secondary.main" }} variant="subtitle1">
                        주 {workingHours}시간
                      </Typography>
                      <Typography sx={{ color: "text.secondary" }} variant="body2">
                        (4주 총 {4*workingHours}시간)
                      </Typography>
                    </Stack>
                  </Stack>
                </Box>
                {/* 2주 플랜 */}
                <Box
                  sx={{
                    width: 1,
                    p: 3,
                    borderRadius: 2,
                    border: "1px solid rgba(145, 158, 171, 0.24)",
                    ...planTrialSelected && {
                      border: (theme) => `2px solid ${theme.palette.secondary.main}`,
                    },
                  }}
                >
                  <Stack direction="row" alignItems="center" sx={{ mb: 1 }} justifyContent="space-between">
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography variant={smUp ? "h5" : "subtitle1"}>
                        2주 50만원 체험판
                      </Typography>
                      <Typography variant="subtitle2" sx={{ color: "text.disabled" }}>
                        플랜{!planSelected && (' 적용시')}
                      </Typography>
                    </Stack>
                    {planSelected && (<Radio color="secondary" value="trial" checked={planTrialSelected} />)}
                  </Stack>
                  <Typography variant="body2" sx={{ color: "text.secondary", mb: 3 }}>
                    설정한 인건비와 주당 업무 시간을 시급 계산하여 2주간 50만원 만큼만 일해볼 수 있어요.
                  </Typography>
                  <Stack spacing={.5} sx={{ mb: 3 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <SvgIconStyle src={`/icons/icon-coin-mono.svg`} sx={{ color: "text.disabled" }} />
                      <Typography variant="h6">
                        인건비
                      </Typography>
                    </Stack>
                    <Stack direction="row" sx={{ pl: 4 }} spacing={1}>
                      <Typography sx={{ color: "secondary.main" }} variant="subtitle1">
                        {trialSalary}만원
                      </Typography>
                      <Typography sx={{ color: "text.secondary" }} variant="body2">
                        (2주 기준)
                      </Typography>
                    </Stack>
                  </Stack>
                  <Stack spacing={.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <SvgIconStyle src={`/icons/ic_clock.svg`} sx={{ color: "text.disabled" }} />
                      <Typography variant="h6">
                        주당 업무 가능한 시간
                      </Typography>
                    </Stack>
                    <Stack direction="row" sx={{ pl: 4 }} spacing={1}>
                      <Typography sx={{ color: "secondary.main" }} variant="subtitle1">
                        주 {trialWorkingHours}시간
                      </Typography>
                      <Typography sx={{ color: "text.secondary" }} variant="body2">
                        (2주 총 {2*trialWorkingHours}시간)
                      </Typography>
                    </Stack>
                  </Stack>
                </Box>
              </Stack>
            </Stack>
          )}
          {/* with longterm */}
          {longtermSalary > 0 && longtermWorkingHours > 0 && (
            <Stack sx={{ mb: 3 }} spacing={1}>
              <Stack direction="row" spacing={1} alignItems="center">
                <SvgIconStyle src={`/icons/ic_receipt.svg`} sx={{ color: expanded ? "secondary.main" : "text.disabled" }} />
                <Typography variant="h6">
                  업무 조건
                </Typography>
              </Stack>
              <Typography sx={{ pl: 4 }} variant={smUp ? "body1" : "body2"}>
                업무 조건은 사전 미팅 이후 최종 제안 시 변경할 수 있습니다.
              </Typography>
              <Stack sx={{ pl: 4, width: 1}} direction={{ xs: "column", sm: "row" }} alignItems="stretch" spacing={2}>
                {/* 4주 플랜 */}
                <Box
                  sx={{
                    width: 1,
                    p: 3,
                    borderRadius: 2,
                    border: "1px solid rgba(145, 158, 171, 0.24)",
                    ...plan4weeksSelected && {
                      border: (theme) => `2px solid ${theme.palette.secondary.main}`,
                    },
                  }}
                >
                  <Stack direction="row" alignItems="center" sx={{ mb: 1 }} justifyContent="space-between">
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography variant={smUp ? "h6" : "subtitle1"}>
                        4주 정식 계약
                      </Typography>
                      <Typography variant="subtitle2" sx={{ color: "text.disabled" }}>
                        플랜{!planSelected && (' 적용시')}
                      </Typography>
                    </Stack>
                    {planSelected && (<Radio color="secondary" value="4weeks" checked={plan4weeksSelected} />)}
                  </Stack>
                  <Typography variant="body2" sx={{ color: "text.secondary" }} paragraph>
                    설정한 4주 인건비와 주당 업무 시간이에요.
                  </Typography>
                  <Stack spacing={.5} sx={{ mb: 2 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <SvgIconStyle src={`/icons/icon-coin-mono.svg`} sx={{ color: "text.disabled", width: 20, height: 20 }} />
                      <Typography variant="subtitle1">
                        인건비
                      </Typography>
                    </Stack>
                    <Stack direction="row" sx={{ pl: 3.5 }} spacing={1}>
                      <Typography sx={{ color: "secondary.main" }} variant="subtitle1">
                        {salary}만원
                      </Typography>
                      <Typography sx={{ color: "text.secondary" }} variant="body2">
                        (4주 기준)
                      </Typography>
                    </Stack>
                  </Stack>
                  <Stack spacing={.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <SvgIconStyle src={`/icons/ic_clock.svg`} sx={{ color: "text.disabled", width: 20, height: 20 }} />
                      <Typography variant="subtitle1">
                        주당 업무 가능한 시간
                      </Typography>
                    </Stack>
                    <Stack direction="row" sx={{ pl: 3.5 }} spacing={1}>
                      <Typography sx={{ color: "secondary.main" }} variant="subtitle1">
                        주 {workingHours}시간
                      </Typography>
                      <Typography sx={{ color: "text.secondary" }} variant="body2">
                        (4주 총 {4*workingHours}시간)
                      </Typography>
                    </Stack>
                  </Stack>
                </Box>
                {/* 2주 플랜 */}
                {!isResidentWork && (
                <Box
                  sx={{
                    width: 1,
                    p: 3,
                    borderRadius: 2,
                    border: "1px solid rgba(145, 158, 171, 0.24)",
                    ...planTrialSelected && {
                      border: (theme) => `2px solid ${theme.palette.secondary.main}`,
                    },
                  }}
                >
                  <Stack direction="row" alignItems="center" sx={{ mb: 1 }} justifyContent="space-between">
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography variant={smUp ? "h6" : "subtitle1"}>
                        2주 단기 체험판
                      </Typography>
                      <Typography variant="subtitle2" sx={{ color: "text.disabled" }}>
                        플랜{!planSelected && (' 적용시')}
                      </Typography>
                    </Stack>
                    {planSelected && (<Radio color="secondary" value="trial" checked={planTrialSelected} />)}
                  </Stack>
                  <Typography variant="body2" sx={{ color: "text.secondary" }} paragraph>
                    2주 동안만 일해보고, 정식 계약으로 전환할 수 있어요.
                  </Typography>
                  <Stack spacing={.5} sx={{ mb: 2 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <SvgIconStyle src={`/icons/icon-coin-mono.svg`} sx={{ color: "text.disabled", width: 20, height: 20 }} />
                      <Typography variant="subtitle1">
                        인건비
                      </Typography>
                    </Stack>
                    <Stack direction="row" sx={{ pl: 3.5 }} spacing={1}>
                      <Typography sx={{ color: "secondary.main" }} variant="subtitle1">
                        {trialSalary}만원
                      </Typography>
                      <Typography sx={{ color: "text.secondary" }} variant="body2">
                        (2주 기준)
                      </Typography>
                    </Stack>
                  </Stack>
                  <Stack spacing={.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <SvgIconStyle src={`/icons/ic_clock.svg`} sx={{ color: "text.disabled", width: 20, height: 20 }} />
                      <Typography variant="subtitle1">
                        주당 업무 가능한 시간
                      </Typography>
                    </Stack>
                    <Stack direction="row" sx={{ pl: 3.5 }} spacing={1}>
                      <Typography sx={{ color: "secondary.main" }} variant="subtitle1">
                        주 {trialWorkingHours}시간
                      </Typography>
                      <Typography sx={{ color: "text.secondary" }} variant="body2">
                        (2주 총 {2*trialWorkingHours}시간)
                      </Typography>
                    </Stack>
                  </Stack>
                </Box>
                )}
                {/* 8주 플랜 */}
                <Box
                  sx={{
                    width: 1,
                    p: 3,
                    borderRadius: 2,
                    border: "1px solid rgba(145, 158, 171, 0.24)",
                    ...planLongtermSelected && {
                      border: (theme) => `2px solid ${theme.palette.secondary.main}`,
                    },
                  }}
                >
                  <Stack direction="row" alignItems="center" sx={{ mb: 1 }} justifyContent="space-between">
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography variant={smUp ? "h6" : "subtitle1"}>
                        8주 장기 계약
                      </Typography>
                      <Typography variant="subtitle2" sx={{ color: "text.disabled" }}>
                        플랜{!planSelected && (' 적용시')}
                      </Typography>
                    </Stack>
                    {planSelected && (<Radio color="secondary" value="trial" checked={planLongtermSelected} />)}
                  </Stack>
                  <Typography variant="body2" sx={{ color: "text.secondary" }} paragraph>
                    업무 내용에 따라 장기 계약할 수 있어요.
                  </Typography>
                  <Stack spacing={.5} sx={{ mb: 2 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <SvgIconStyle src={`/icons/icon-coin-mono.svg`} sx={{ color: "text.disabled", width: 20, height: 20 }} />
                      <Typography variant="subtitle1">
                        인건비
                      </Typography>
                    </Stack>
                    <Stack direction="row" sx={{ pl: 3.5 }} spacing={1}>
                      <Typography sx={{ color: "secondary.main" }} variant="subtitle1">
                        {longtermSalary}만원
                      </Typography>
                      <Typography sx={{ color: "text.secondary" }} variant="body2">
                        (8주 기준)
                      </Typography>
                    </Stack>
                  </Stack>
                  <Stack spacing={.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <SvgIconStyle src={`/icons/ic_clock.svg`} sx={{ color: "text.disabled", width: 20, height: 20 }} />
                      <Typography variant="subtitle1">
                        주당 업무 가능한 시간
                      </Typography>
                    </Stack>
                    <Stack direction="row" sx={{ pl: 3.5 }} spacing={1}>
                      <Typography sx={{ color: "secondary.main" }} variant="subtitle1">
                        주 {longtermWorkingHours}시간
                      </Typography>
                      <Typography sx={{ color: "text.secondary" }} variant="body2">
                        (8주 총 {8*longtermWorkingHours}시간)
                      </Typography>
                    </Stack>
                  </Stack>
                </Box>
              </Stack>
            </Stack>
          )}
          {questionAnswers?.answers?.length > 0 && (
            <Stack sx={{ mb: 3 }} spacing={1}>
              <Stack direction="row" spacing={1} alignItems="center">
                <SvgIconStyle src={`/icons/icon-mic-mono.svg`} sx={{ color: expanded ? "secondary.main" : "text.disabled" }} />
                <Typography variant="h6">
                  <DIOText /> 인터뷰 답변
                </Typography>
              </Stack>
              <Stack sx={{ pl: 4 }} spacing={2} divider={<Divider />}>
                {questionAnswers.answers.map((answer, index) => (
                  <Stack key={index} spacing={1}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={smUp ? 1 : 0}>
                      <Typography variant="h6" sx={{ color: "secondary.lighter" }}>
                        Q{index+1}.
                      </Typography>
                      <Typography variant="h6" sx={{ whiteSpace: "pre-wrap" }}>
                        {answer.question}
                      </Typography>
                    </Stack>
                    {answer.question_type === "yes-no" ? (
                      <Stack sx={{ pl: smUp ? 4.5 : 0 }} spacing={.5}>
                        {answer.yn_answer !== null && (
                        <Typography variant="body1" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-all" }}>
                          {answer.yn_answer}
                        </Typography>
                        )}
                        {answer.yn_answer !== null && (
                        <Typography variant="subtitle1" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-all" }}>
                          {answer.yn_answer === "네" ? answer.yes_followup : answer.no_followup}
                        </Typography>
                        )}
                        <Typography sx={{ whiteSpace: "pre-wrap", wordBreak: "break-all" }}>
                          {answer.answer}
                        </Typography>
                      </Stack>
                    ) : (
                      <Typography sx={{ pl: smUp ? 4.5 : 0, whiteSpace: "pre-wrap", wordBreak: "break-all" }}>
                        {answer.answer}
                      </Typography>
                    )}
                  </Stack>
                ))}
              </Stack>
            </Stack>
          )}
          {suggestion && (
            <Stack sx={{ mb: 3 }} spacing={1}>
              <Stack direction="row" spacing={1} alignItems="center">
                <SvgIconStyle src={`/icons/icon-pencil-mono.svg`} sx={{ color: expanded ? "secondary.main" : "text.disabled" }} />
                <Typography variant="h6">
                  질문 또는 업무 범위 수정 제안
                </Typography>
              </Stack>
              <Typography sx={{ pl: 4, whiteSpace: "pre-wrap" }} variant="body1">
                {suggestion}
              </Typography>
            </Stack>
          )}
          {timeRange && before3WayStatus.includes(status) && (
            <Stack sx={{ mb: 3 }} spacing={1}>
              <Stack direction="row" spacing={1} alignItems="center">
                <SvgIconStyle src={`/icons/ic_videocam.svg`} sx={{ color: expanded ? "secondary.main" : "text.disabled" }} />
                <Typography variant="h6">
                  사전 미팅 가능 시간대 (한국시간 기준)
                </Typography>
              </Stack>
              {typeof timeRange === 'object' && Array.isArray(timeRange) && (
                <Stack
                  sx={{
                    backgroundColor: (theme) => theme.palette.grey[100],
                    borderRadius: 1.5,
                    p: 3,
                  }}
                >
                  {timeRange.map((time, index) => (
                    <Typography sx={{ pl: 1 }} key={index} gutterBottom>
                      <b>{fKrMDEEEEKST(`${time?.date} 00:00:00`)}</b> {time?.intervals?.map((item) => `${Number.parseInt(item.start.slice(0,2))}시`).join(', ')}
                    </Typography>
                  ))}
                </Stack>
              )}
            </Stack>
          )}
        </Box>
      </Card>
      </AccordionDetails>
    </Accordion>
    </>
  )
}
